import moment from 'moment';
import { DateRange } from './type';
import ApplicationDomainUtility from 'v3/common/utils/applicationDomain';
export const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
export const years = Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - i);

const buildRange = (
  days: number,
  unit: moment.unitOfTime.DurationConstructor,
  startDate: Date | null,
  endDate: Date | null
) => {
  const start = moment(startDate);
  const end = moment(endDate);
  const maxStart = end.clone().subtract(days, unit);
  const finalStart = start.isBefore(maxStart) ? maxStart : start;

  return {
    startDate: finalStart.toDate(),
    endDate: end.clone().toDate(),
  };
};

export const predefinedRanges = (startDate: Date | null, endDate: Date | null) => {
  const endDateMoment = moment(endDate);
  const today = moment();
  const isSameDay = (a: moment.Moment, b: moment.Moment) => a.isSame(b, 'day');
  const isYesterday = (start: moment.Moment, end: moment.Moment) => {
    if (start.isSame(end, 'day')) return true;
    if (start.clone().subtract(1, 'day').isSame(end, 'day')) return true;
    return false;
  };

  return [
    {
      label: 'Today',
      range: () => {
        return {
          startDate: today.toDate(),
          endDate: today.toDate(),
        };
      },
      isShow: isSameDay(today, endDateMoment),
    },
    {
      label: 'Yesterday',
      range: () => getAdjustedYesterday(),
      isShow: isYesterday(today, endDateMoment),
    },
    {
      label: 'Last 3 days',
      range: () => buildRange(2, 'days', startDate, endDate),
      isShow: endDateMoment.isAfter(moment().subtract(2, 'days')),
    },
    {
      label: 'Last week',
      range: () => buildRange(1, 'week', startDate, endDate),
      isShow: true,
    },
    {
      label: 'Last month',
      range: () => buildRange(1, 'month', startDate, endDate),
      isShow: true,
    },
    {
      label: 'Last 3 months',
      range: () => buildRange(3, 'months', startDate, endDate),
      isShow: true,
    },
    {
      label: 'Last year',
      range: () => buildRange(1, 'year', startDate, endDate),
      isShow: true,
    },
    {
      label: 'Custom',
      range: () => ({
        startDate: null,
        endDate: null,
      }),
      isShow: true,
    },
  ];
};

export const getAdjustedYesterday = () => {
  let date = moment().subtract(1, 'day');

  if (!ApplicationDomainUtility.isCrypto) {
    while (date.day() === 0 || date.day() === 6) {
      date = date.subtract(1, 'day');
    }
  }

  return {
    startDate: date.clone().startOf('day').toDate(),
    endDate: date.clone().endOf('day').toDate(),
  };
};

export const formattedDateRange = ({ startDate, endDate }: DateRange) => {
  return {
    startRange: moment(startDate).format('DD-MMM-YYYY'),
    endRange: moment(endDate).format('DD-MMM-YYYY'),
    dayDifference: dayDifference(startDate, endDate),
  };
};

const dayDifference = (startDate: Date | null, endDate: Date | null): string | number => {
  if (!startDate || !endDate) return 0;

  const start = moment(startDate).startOf('day');
  const end = moment(endDate).startOf('day');
  const today = moment().startOf('day');

  const diffDays = end.diff(start, 'days');
  const isEndYesterday = end.isSame(today.clone().subtract(1, 'day'), 'day');
  const isEndToday = end.isSame(today, 'day');

  if (diffDays === 0) {
    if (isEndToday) return 'Today';
    if (isEndYesterday) return 'Yesterday';
    return 'Same day';
  }

  if (diffDays === 1 && isEndYesterday) return 'Yesterday';

  return end.from(start, true);
};
